import { isAnyCarsVehiclesCategory, isAnyRealEstateCategory } from '@kijiji/category'
import { AuthModalVariant, useContactSellerMutation } from '@kijiji/generated/graphql-types'
import { useRouter } from 'next/router'
import { useSession } from 'next-auth/react'
import { Trans, useTranslation } from 'next-i18next'
import { type ChangeEventHandler, type FC, type MouseEventHandler, useRef, useState } from 'react'
import { useTheme } from 'styled-components'

import { LinkCustom } from '@/components/shared/link-custom/LinkCustom'
import { TRANSLATION_KEYS } from '@/constants/localization'
import { isUserAuthenticated } from '@/features/auth/constants/user'
import { useSignInModal } from '@/features/auth/hooks/useSignInModal'
import {
  type R2SStructuredMessagesProps,
  R2SStructuredMessages,
} from '@/features/listing/components/reply-to-seller/conversations/R2SStructuredMessages'
import { ListingsSimilarModal } from '@/features/listing/components/similar/ListingsSimilarModal'
import { getMyConversationUrl } from '@/features/listing/utils/reply-to-seller/getMyConversationUrl'
import { getR2SSuccessMessage } from '@/features/listing/utils/reply-to-seller/getR2SSuccessMessage'
import { trackEvent } from '@/lib/ga'
import { GA_EVENT } from '@/lib/ga/constants/gaEvent'
import { BodyText } from '@/ui/atoms/body-text'
import { Button } from '@/ui/atoms/button'
import { Flex } from '@/ui/atoms/flex'
import { SystemMessage } from '@/ui/molecules/system-message'
import { TextAreaField } from '@/ui/molecules/textarea-field'
import { sendToLogger } from '@/utils/sendToLogger'

export type R2SFormProps = Pick<R2SStructuredMessagesProps, 'structuredMessages'> & {
  /**
   * Listing's category Id
   * */
  categoryId: number

  /**
   * Specifies if the R2S Form form should be disabled
   */
  disableForm?: boolean

  /**
   * Initial value for the message field.
   */
  initialMessage?: string
  /**
   * It should define if the seller of this listing is a professional seller
   * Dealer, KMB or Professional
   */
  isProfessionalSeller?: boolean
  listingId: string
  /**
   * Callback to be executed after the form is successfully submitted
   */
  successCallbackFn?: () => void
}

enum FORM_FIELDS {
  MESSAGE = 'message',
}

/**
 * Form used by the R2S feature to send a message to a seller.
 */
export const R2SForm: FC<R2SFormProps> = ({
  categoryId,
  disableForm,
  initialMessage,
  isProfessionalSeller,
  listingId,
  structuredMessages,
  successCallbackFn,
}) => {
  /** It fires on focus only once */
  const hasR2SEmailBeginFired = useRef<boolean>(false)

  const { t } = useTranslation([
    TRANSLATION_KEYS.R2S,
    TRANSLATION_KEYS.ROUTES,
    TRANSLATION_KEYS.COMMON,
  ])

  const { colors, spacing } = useTheme()
  const { status } = useSession()
  const { asPath } = useRouter()

  const { openSignInModal } = useSignInModal()

  const [fields, setFields] = useState<Record<FORM_FIELDS, string>>({
    [FORM_FIELDS.MESSAGE]: initialMessage ?? '',
  })
  const [errors, setErrors] = useState<Record<FORM_FIELDS | 'submit', string>>({
    [FORM_FIELDS.MESSAGE]: '',
    submit: '',
  })
  const [loading, setLoading] = useState<boolean>(false)
  /** The state will contain the conversation ID created when the form is submitted */
  const [submittedChatId, setSubmittedChatId] = useState<string>()

  const [contactSeller] = useContactSellerMutation()

  const [isListingsSimilarModalOpen, setIsListingsSimilarModalOpen] = useState(false)
  const shouldShowListingsSimilarModal =
    isAnyRealEstateCategory(categoryId) || isAnyCarsVehiclesCategory(categoryId)

  const handleSimilarModalClose = () => setIsListingsSimilarModalOpen(false)

  const handleSendMessage: MouseEventHandler<HTMLButtonElement> = async (e) => {
    e.preventDefault()

    trackEvent({
      action: GA_EVENT.R2SEmailAttempt,
      label: `phone=false;categoryid=${categoryId};`,
    })

    if (!isUserAuthenticated(status)) {
      openSignInModal({ modalVariant: AuthModalVariant.LoginToMessage, callbackUrl: asPath })
      return
    }

    /** Validation */
    if (!fields.message.trim()) {
      setErrors((prev) => ({
        ...prev,
        [FORM_FIELDS.MESSAGE]: t('common:forms.inputs.message.errors.required'),
      }))
      trackEvent({ action: GA_EVENT.R2SEmailFail, label: 'Validation Error' })
      return
    }

    setErrors({ message: '', submit: '' })
    setLoading(true)

    try {
      const { data } = await contactSeller({
        variables: { listingContactId: listingId, message: fields.message },
      })

      setSubmittedChatId(data?.listingContact ?? undefined)

      trackEvent({
        action: GA_EVENT.R2SEmailSuccess,
        label: `phone=false;categoryid=${categoryId};`,
      })

      if (shouldShowListingsSimilarModal) {
        setIsListingsSimilarModalOpen(true)
      }

      if (successCallbackFn) successCallbackFn()
    } catch (err) {
      sendToLogger(err, { fingerprint: ['useContactSeller'] })
      setErrors((prev) => ({ ...prev, submit: t('r2s:message_form.error.message') }))

      trackEvent({ action: GA_EVENT.R2SEmailFail })
    } finally {
      setLoading(false)
    }
  }

  const handleFormFieldChange: ChangeEventHandler<HTMLTextAreaElement | HTMLInputElement> = (e) => {
    const value = e.target.value

    const targetId = e.target.id as FORM_FIELDS

    /** Validate targetId is defined as one of the expected form fields */
    if (Object.values(FORM_FIELDS).includes(targetId)) {
      setFields((prev) => ({ ...prev, [targetId]: value }))
    }
  }

  const handleFocus = () => {
    if (!hasR2SEmailBeginFired.current) {
      hasR2SEmailBeginFired.current = true
      trackEvent({ action: GA_EVENT.R2SEmailBegin, label: 'guest_msg=false' })
    }
  }

  const handleStructureMessageClick = (message: string) => {
    setFields((prev) => ({ ...prev, message }))

    /**
     * I can't use ref to focus the text-area because the input elements from the UI library
     * already define refs inside of them to control the floating label.
     *
     * The only way I can focus this element on click is directly manipulating the document
     */
    const textArea = document.getElementById(FORM_FIELDS.MESSAGE)
    if (textArea) textArea.focus()
  }

  const chatUrl = getMyConversationUrl(submittedChatId)
  const successMessage = getR2SSuccessMessage(t, categoryId, isProfessionalSeller)

  const isFormVisible = isUserAuthenticated(status) && !submittedChatId

  return (
    <>
      <R2SStructuredMessages
        structuredMessages={structuredMessages}
        handleStructureMessageClick={handleStructureMessageClick}
        isVisible={hasR2SEmailBeginFired.current}
      />
      <Flex
        as={isFormVisible ? 'form' : 'div'}
        flexDirection="column"
        gap={spacing.default}
        justifyContent="space-between"
        style={{ height: '100%' }}
        data-testid="r2s-form"
      >
        <Flex flexDirection="column" gap={spacing.default}>
          {submittedChatId ? (
            <SystemMessage
              description={successMessage.description}
              hideCloseButton
              title={successMessage.title}
              variation="success"
            />
          ) : null}

          {errors.submit ? (
            <SystemMessage description={errors.submit} hideCloseButton variation="error" />
          ) : null}

          {isFormVisible ? (
            <TextAreaField
              bottom="0"
              error={errors.message}
              id={FORM_FIELDS.MESSAGE}
              label={t('r2s:message_form.message.label')}
              onChange={handleFormFieldChange}
              onFocus={handleFocus}
              value={fields[FORM_FIELDS.MESSAGE]}
            />
          ) : null}
        </Flex>

        <Flex flexDirection="column" gap={spacing.defaultSmall}>
          {submittedChatId ? (
            <Button as="a" href={chatUrl} isFullWidth target="_blank" variant="secondary">
              {t('message_form.send_other_message.label')}
            </Button>
          ) : (
            <Button
              type="submit"
              isFullWidth
              isLoading={loading}
              onClick={handleSendMessage}
              disabled={disableForm}
            >
              {t('r2s:message_form.submit_button.label')}
            </Button>
          )}

          <BodyText size="xSmall" color={colors.grey.light1}>
            <Trans
              i18nKey="r2s:message_form.disclaimer"
              components={[
                <LinkCustom
                  hasUnderline
                  href={t('routes:external.terms_of_use.href')}
                  key="termsLink"
                  noChildren
                  rel="noreferrer"
                  size="xSmall"
                  target="_blank"
                  variant="secondary"
                />,
                <LinkCustom
                  hasUnderline
                  href={t('routes:external.privacy_policy.href')}
                  key="privacyLink"
                  noChildren
                  rel="noreferrer"
                  size="xSmall"
                  target="_blank"
                  variant="secondary"
                />,
              ]}
            />
          </BodyText>
        </Flex>
      </Flex>
      <ListingsSimilarModal
        isOpen={isListingsSimilarModalOpen}
        handleClose={handleSimilarModalClose}
        listingId={listingId}
        successMessage={successMessage}
      />
    </>
  )
}
